import React, { useState, useEffect } from 'react';
import { tournamentsAPI, myTournamentsAPI, adminAPI, familyArchersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatDateRange, getLevelColor, getOrgColor, getDaysUntil } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Search,
  Filter,
  Calendar,
  MapPin,
  Trophy,
  Plus,
  ExternalLink,
  Target,
  CheckCircle,
  RefreshCw,
  Clock,
  ListFilter,
  User
} from 'lucide-react';
import { toast } from 'sonner';

const ORGANIZATIONS = ['ALL', 'USAA', 'USAT', 'NFAA', 'TSAA', 'TFAA', 'ASA', 'IBO'];
const LEVELS = ['ALL', 'Local', 'State', 'Regional', 'National', 'JOAD', 'Youth'];
const EVENT_TYPES = ['ALL', 'Target', 'Field', '3D', 'Indoor'];

export default function Tournaments() {
  const { isAdmin, user } = useAuth();
  const isParent = user?.role === 'parent';
  const [tournaments, setTournaments] = useState([]);
  const [familyArchers, setFamilyArchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [scrapeResults, setScrapeResults] = useState(null);
  const [showScrapeResults, setShowScrapeResults] = useState(false);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(true);
  const [filters, setFilters] = useState({
    org: 'ALL',
    level: 'ALL',
    event_type: 'ALL',
    days_ahead: 365
  });
  const [trackingTournament, setTrackingTournament] = useState(null);
  const [trackingStatus, setTrackingStatus] = useState('INTERESTED');
  const [trackingNotes, setTrackingNotes] = useState('');
  const [trackingEventTime, setTrackingEventTime] = useState('');
  const [trackingEventTimezone, setTrackingEventTimezone] = useState('');
  const [trackingArcherId, setTrackingArcherId] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchTournaments();
    fetchLastUpdated();
    if (isParent) {
      fetchFamilyArchers();
    }
  }, [filters, showAll, isParent]);

  const fetchFamilyArchers = async () => {
    try {
      const response = await familyArchersAPI.getAll();
      setFamilyArchers(response.data);
    } catch (error) {
      console.error('Failed to fetch family archers');
    }
  };

  const fetchLastUpdated = async () => {
    try {
      const response = await tournamentsAPI.getLastUpdated();
      setLastUpdated(response.data);
    } catch (error) {
      console.error('Failed to fetch last updated');
    }
  };

  const handleRefreshTournaments = async () => {
    setRefreshing(true);
    setScrapeResults(null);
    try {
      await adminAPI.triggerScrape();
      toast.success('Tournament refresh started! Checking for new tournaments...');
      
      // Poll for completion
      let attempts = 0;
      const maxAttempts = 30;
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const statusRes = await tournamentsAPI.getLastUpdated();
          const status = statusRes.data;
          
          if (status.status === 'completed') {
            clearInterval(pollInterval);
            setRefreshing(false);
            await fetchTournaments();
            await fetchLastUpdated();
            
            // Show results
            const stats = status.last_scrape_stats || {};
            setScrapeResults({
              total: stats.scraped || 0,
              new: stats.new || 0,
              updated: stats.updated || 0,
              byOrg: stats.by_org || {},
              newTournaments: status.new_tournaments || []
            });
            setShowScrapeResults(true);
            
            if (stats.new > 0) {
              toast.success(`Found ${stats.new} new tournament${stats.new > 1 ? 's' : ''}!`);
            } else {
              toast.info('No new tournaments found. All data is up to date.');
            }
          } else if (status.status === 'failed') {
            clearInterval(pollInterval);
            setRefreshing(false);
            toast.error('Scrape failed: ' + (status.error || 'Unknown error'));
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setRefreshing(false);
            toast.warning('Scrape taking longer than expected. Check back later.');
          }
        } catch (e) {
          // Continue polling
        }
      }, 2000);
      
    } catch (error) {
      toast.error('Failed to start refresh');
      setRefreshing(false);
    }
  };

  const fetchTournaments = async () => {
    setLoading(true);
    try {
      const params = {
        days_ahead: showAll ? 730 : filters.days_ahead  // 2 years when "Show All" is on
      };
      if (filters.org !== 'ALL') params.org = filters.org;
      if (filters.level !== 'ALL') params.level = filters.level;
      if (filters.event_type !== 'ALL') params.event_type = filters.event_type;

      const response = await tournamentsAPI.getAll(params);
      setTournaments(response.data);
    } catch (error) {
      toast.error('Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  const handleTrack = async () => {
    if (!trackingTournament) return;

    // For parents, require archer selection if they have archers
    if (isParent && familyArchers.length > 0 && !trackingArcherId) {
      toast.error('Please select which archer is attending this tournament');
      return;
    }

    const selectedArcher = familyArchers.find(a => a.id === trackingArcherId);

    try {
      await myTournamentsAPI.track({
        tournament_id: trackingTournament.id,
        status: trackingStatus,
        notes: trackingNotes,
        event_time: trackingEventTime || null,
        event_timezone: trackingEventTimezone || null,
        archer_id: trackingArcherId || null,
        archer_name: selectedArcher?.name || null
      });
      toast.success(`Tournament ${trackingStatus === 'REGISTERED' ? 'registered' : 'added to interests'}!`);
      setDialogOpen(false);
      setTrackingTournament(null);
      setTrackingNotes('');
      setTrackingEventTime('');
      setTrackingEventTimezone('');
      setTrackingArcherId('');
    } catch (error) {
      toast.error('Failed to track tournament');
    }
  };

  const filteredTournaments = tournaments.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.location.toLowerCase().includes(search.toLowerCase())
  );

  const formatLastUpdated = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffHours < 48) return 'Yesterday';
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tournaments-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Tournaments</h1>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-slate-600">Browse and track upcoming archery tournaments</p>
            {lastUpdated && (
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Updated {formatLastUpdated(lastUpdated.last_scrape)}
              </span>
            )}
          </div>
        </div>
        {isAdmin && (
          <Button
            variant="outline"
            onClick={handleRefreshTournaments}
            disabled={refreshing}
            data-testid="refresh-tournaments-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh Data'}
          </Button>
        )}
      </div>

      {/* Auto-refresh info banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-3">
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <RefreshCw className="w-4 h-4" />
            <span>
              Tournament data is automatically refreshed daily at 6:00 AM UTC from USAA, USAT, NFAA, TSAA, and TFAA.
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Scrape Results Dialog */}
      <Dialog open={showScrapeResults} onOpenChange={setShowScrapeResults}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-green-600" />
              Scrape Results
            </DialogTitle>
          </DialogHeader>
          {scrapeResults && (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-slate-900">{scrapeResults.total}</div>
                  <div className="text-xs text-slate-500">Total Scraped</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-600">{scrapeResults.new}</div>
                  <div className="text-xs text-green-700">New Added</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-600">{scrapeResults.updated}</div>
                  <div className="text-xs text-blue-700">Updated</div>
                </div>
              </div>

              {/* By Organization */}
              {scrapeResults.byOrg && Object.keys(scrapeResults.byOrg).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">By Organization</h4>
                  <div className="space-y-1">
                    {Object.entries(scrapeResults.byOrg).map(([org, stats]) => (
                      <div key={org} className="flex items-center justify-between text-sm py-1 px-2 bg-slate-50 rounded">
                        <span className="font-medium">{org}</span>
                        <div className="flex gap-3">
                          <span className="text-slate-600">{stats.total} total</span>
                          {stats.new > 0 && (
                            <span className="text-green-600 font-medium">+{stats.new} new</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New Tournaments List */}
              {scrapeResults.newTournaments && scrapeResults.newTournaments.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">New Tournaments Found</h4>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {scrapeResults.newTournaments.map((t, idx) => (
                      <div key={idx} className="p-2 bg-green-50 border border-green-100 rounded text-sm">
                        <div className="font-medium text-slate-900">{t.name}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          {t.org} • {t.start_date} • {t.location}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {scrapeResults.new === 0 && (
                <div className="text-center py-4 text-slate-500">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <p>All tournament data is up to date.</p>
                  <p className="text-xs mt-1">No new tournaments were found from the sources.</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowScrapeResults(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Search and Filters */}
      <Card className="card-base">
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search tournaments by name or location..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 input-base"
                data-testid="tournament-search"
              />
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <Select
                value={filters.org}
                onValueChange={(value) => setFilters({ ...filters, org: value })}
              >
                <SelectTrigger className="w-32" data-testid="org-filter">
                  <SelectValue placeholder="Organization" />
                </SelectTrigger>
                <SelectContent>
                  {ORGANIZATIONS.map((org) => (
                    <SelectItem key={org} value={org}>
                      {org === 'ALL' ? 'All Orgs' : org}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.level}
                onValueChange={(value) => setFilters({ ...filters, level: value })}
              >
                <SelectTrigger className="w-32" data-testid="level-filter">
                  <SelectValue placeholder="Level" />
                </SelectTrigger>
                <SelectContent>
                  {LEVELS.map((level) => (
                    <SelectItem key={level} value={level}>
                      {level === 'ALL' ? 'All Levels' : level}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.event_type}
                onValueChange={(value) => setFilters({ ...filters, event_type: value })}
              >
                <SelectTrigger className="w-32" data-testid="type-filter">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type === 'ALL' ? 'All Types' : type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {!showAll && (
                <Select
                  value={String(filters.days_ahead)}
                  onValueChange={(value) => setFilters({ ...filters, days_ahead: parseInt(value) })}
                >
                  <SelectTrigger className="w-36" data-testid="days-filter">
                    <SelectValue placeholder="Time Range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="90">Next 90 days</SelectItem>
                    <SelectItem value="180">Next 6 months</SelectItem>
                    <SelectItem value="365">Next year</SelectItem>
                  </SelectContent>
                </Select>
              )}

              {/* Show All Toggle */}
              <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
                <ListFilter className="w-4 h-4 text-slate-500" />
                <Label htmlFor="show-all" className="text-sm font-medium text-slate-700 cursor-pointer">
                  Show All
                </Label>
                <Switch
                  id="show-all"
                  checked={showAll}
                  onCheckedChange={setShowAll}
                  data-testid="show-all-toggle"
                />
              </div>
            </div>
          </div>
          
          {/* Results count */}
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Showing <span className="font-medium text-slate-700">{filteredTournaments.length}</span> tournament{filteredTournaments.length !== 1 ? 's' : ''}
              {!showAll && <span> in the next {filters.days_ahead} days</span>}
              {showAll && <span> (all upcoming)</span>}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tournament List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-slate-600">Loading tournaments...</p>
          </div>
        </div>
      ) : filteredTournaments.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredTournaments.map((tournament) => {
            const daysUntil = getDaysUntil(tournament.start_date);
            const deadlineDays = tournament.registration_deadline
              ? getDaysUntil(tournament.registration_deadline)
              : null;

            return (
              <Card
                key={tournament.id}
                className="card-base hover:border-primary/50"
                data-testid={`tournament-card-${tournament.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 text-lg truncate">
                        {tournament.name}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={getOrgColor(tournament.org)}>{tournament.org}</Badge>
                        <Badge className={getLevelColor(tournament.level)}>{tournament.level}</Badge>
                        <Badge variant="outline">{tournament.event_type}</Badge>
                      </div>
                    </div>
                    <Dialog open={dialogOpen && trackingTournament?.id === tournament.id} onOpenChange={setDialogOpen}>
                      <DialogTrigger asChild>
                        <Button
                          size="sm"
                          className="btn-primary ml-4"
                          onClick={() => {
                            setTrackingTournament(tournament);
                            setDialogOpen(true);
                          }}
                          data-testid={`track-btn-${tournament.id}`}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Track
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Track Tournament</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="p-4 bg-slate-50 rounded-lg">
                            <h4 className="font-medium text-slate-900">{trackingTournament?.name}</h4>
                            <p className="text-sm text-slate-500 mt-1">
                              {formatDateRange(trackingTournament?.start_date, trackingTournament?.end_date)}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <Label>Status</Label>
                            <Select value={trackingStatus} onValueChange={setTrackingStatus}>
                              <SelectTrigger data-testid="tracking-status-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="INTERESTED">Interested</SelectItem>
                                <SelectItem value="REGISTERED">Registered</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {/* Archer selector for parents */}
                          {isParent && familyArchers.length > 0 && (
                            <div className="space-y-2">
                              <Label className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                Which Archer? *
                              </Label>
                              <Select value={trackingArcherId} onValueChange={setTrackingArcherId}>
                                <SelectTrigger data-testid="tracking-archer-select">
                                  <SelectValue placeholder="Select an archer" />
                                </SelectTrigger>
                                <SelectContent>
                                  {familyArchers.map((archer) => (
                                    <SelectItem key={archer.id} value={archer.id}>
                                      {archer.name} {archer.age_class && `(${archer.age_class})`}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-slate-500">
                                Select which family archer will attend this tournament.
                              </p>
                            </div>
                          )}
                          
                          {isParent && familyArchers.length === 0 && (
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-sm text-amber-800">
                                <strong>Tip:</strong> Add your archers in the{' '}
                                <a href="/family-archers" className="underline text-amber-900">Family Archers</a>{' '}
                                page to track tournaments for each archer separately.
                              </p>
                            </div>
                          )}

                          <div className="space-y-2">
                            <Label>Event Time (optional)</Label>
                            <div className="flex gap-2">
                              <Input
                                type="time"
                                placeholder="e.g., 09:00 AM"
                                value={trackingEventTime}
                                onChange={(e) => setTrackingEventTime(e.target.value)}
                                data-testid="tracking-event-time"
                                className="input-base flex-1"
                              />
                              <Select value={trackingEventTimezone} onValueChange={setTrackingEventTimezone}>
                                <SelectTrigger className="w-[140px]" data-testid="tracking-timezone-select">
                                  <SelectValue placeholder="Timezone" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="America/New_York">Eastern (ET)</SelectItem>
                                  <SelectItem value="America/Chicago">Central (CT)</SelectItem>
                                  <SelectItem value="America/Denver">Mountain (MT)</SelectItem>
                                  <SelectItem value="America/Los_Angeles">Pacific (PT)</SelectItem>
                                  <SelectItem value="America/Phoenix">Arizona (AZ)</SelectItem>
                                  <SelectItem value="Pacific/Honolulu">Hawaii (HT)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <p className="text-xs text-slate-500">When are you scheduled to compete? Select the tournament's local timezone.</p>
                          </div>
                          <div className="space-y-2">
                            <Label>Notes <span className="text-slate-400 font-normal">(Add any details that may help with tournament registration, preparation, or coordination)</span></Label>
                            <Textarea
                              placeholder="Add any notes..."
                              value={trackingNotes}
                              onChange={(e) => setTrackingNotes(e.target.value)}
                              data-testid="tracking-notes"
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button className="btn-primary" onClick={handleTrack} data-testid="confirm-track-btn">
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Track Tournament
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>

                  <div className="space-y-2 text-sm text-slate-600">
                    <p className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      {formatDateRange(tournament.start_date, tournament.end_date)}
                      {daysUntil !== null && daysUntil >= 0 && (
                        <Badge variant="outline" className="ml-2">
                          {daysUntil === 0 ? 'Today' : `${daysUntil} days`}
                        </Badge>
                      )}
                    </p>
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      {tournament.location}
                    </p>
                    {tournament.registration_deadline && (
                      <p className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-slate-400" />
                        Registration deadline: {formatDate(tournament.registration_deadline)}
                        {deadlineDays !== null && deadlineDays >= 0 && deadlineDays <= 14 && (
                          <Badge
                            variant="outline"
                            className={
                              deadlineDays <= 3
                                ? 'bg-red-100 text-red-700 border-red-200'
                                : 'bg-amber-100 text-amber-700 border-amber-200'
                            }
                          >
                            {deadlineDays} days left
                          </Badge>
                        )}
                      </p>
                    )}
                  </div>

                  {tournament.description && (
                    <p className="mt-3 text-sm text-slate-500 line-clamp-2">
                      {tournament.description}
                    </p>
                  )}

                  {tournament.registration_url && (
                    <a
                      href={tournament.registration_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-3 text-sm text-primary hover:underline"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Register online
                    </a>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="card-base">
          <CardContent className="py-12 text-center">
            <Trophy className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No tournaments found</h3>
            <p className="text-slate-500">
              Try adjusting your filters or search terms
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
